
import React, { createContext, useState, useContext, useEffect } from 'react';

const ArticleContext = createContext();

export const ArticleProvider = ({ children }) => {
  // 从 localStorage 读取文章数据，如果没有则使用初始数据
  const getInitialArticles = () => {
    const savedArticles = localStorage.getItem('blog_articles');
    if (savedArticles) {
      try {
        return JSON.parse(savedArticles);
      } catch (error) {
        console.error('解析文章数据失败:', error);
        return getDefaultArticles();
      }
    }
    return getDefaultArticles();
  };

  // 默认文章数据
  const getDefaultArticles = () => {
    return [
      {
        id: 1,
        title: 'React Hooks 深度解析',
        excerpt: '深入探讨React Hooks的原理和最佳实践，包括useState、useEffect、useContext等常用Hook的使用技巧。',
        date: '2023-10-20',
        author: 'zxj',
        readTime: '8分钟',
        tags: ['React', '前端', 'JavaScript'],
        category: '前端',
        content: `
          <h2>React简介</h2>
          <p>React是由Facebook开发的一个用于构建用户界面的JavaScript库。它以其组件化、声明式和高效的特性在前端开发领域广受欢迎。</p>
          
          <h2>为什么选择React？</h2>
          <p>React让构建交互式UI变得简单。通过组件化的开发模式，你可以轻松构建复杂的用户界面。</p>
          
          <h2>核心概念</h2>
          <ul>
            <li><strong>组件(Components)：</strong>React应用的基本构建块</li>
            <li><strong>JSX：</strong>JavaScript的语法扩展，让你在JavaScript中编写类似HTML的代码</li>
            <li><strong>Props：</strong>组件之间传递数据的方式</li>
            <li><strong>State：</strong>组件内部的状态管理</li>
            <li><strong>Hooks：</strong>让你在函数组件中使用状态和其他React特性</li>
          </ul>
        `
      },
      {
        id: 2,
        title: 'TypeScript 类型系统完全指南',
        excerpt: '全面介绍TypeScript的类型系统，从基础类型到高级类型工具，帮助你写出更安全的代码。',
        date: '2023-10-18',
        author: 'zxj',
        readTime: '12分钟',
        tags: ['TypeScript', '编程', 'Web开发'],
        category: 'TypeScript',
        content: `
          <h2>类型系统概述</h2>
          <p>TypeScript是JavaScript的超集，它添加了静态类型系统，让大型应用开发更加可靠。</p>
          
          <h2>基础类型</h2>
          <p>TypeScript支持JavaScript的所有基础类型，并新增了一些类型。</p>
          
          <h2>高级类型</h2>
          <ul>
            <li>联合类型</li>
            <li>交叉类型</li>
            <li>泛型</li>
            <li>条件类型</li>
          </ul>
        `
      }
    ];
  };

  const [articles, setArticles] = useState(getInitialArticles());

  // 当文章数据变化时，保存到 localStorage
  useEffect(() => {
    localStorage.setItem('blog_articles', JSON.stringify(articles));
  }, [articles]);

  // 获取下一个可用的文章ID
  const getNextId = () => {
    if (articles.length === 0) return 1;
    const maxId = Math.max(...articles.map(article => article.id));
    return maxId + 1;
  };

  // 发布新文章
  const publishArticle = (articleData) => {
    return new Promise((resolve) => {
      const newArticle = {
        id: getNextId(),
        ...articleData,
        date: new Date().toISOString().split('T')[0], // 当前日期
        author: articleData.author || 'zxj',
        readTime: calculateReadTime(articleData.content),
        tags: articleData.tags ? articleData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : ['未分类'],
        content: articleData.content || '',
        excerpt: articleData.excerpt || ''
      };
      
      setArticles(prevArticles => [newArticle, ...prevArticles]);
      
      setTimeout(() => {
        resolve(newArticle);
      }, 500);
    });
  };

  // 计算阅读时间
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes}分钟`;
  };

  // 删除文章
  const deleteArticle = (id) => {
    return new Promise((resolve) => {
      setArticles(prevArticles => prevArticles.filter(article => article.id !== id));
      
      setTimeout(() => {
        resolve(true);
      }, 500);
    });
  };

  // 更新文章
  const updateArticle = (id, updatedData) => {
    return new Promise((resolve) => {
      setArticles(prevArticles => 
        prevArticles.map(article => 
          article.id === id ? { ...article, ...updatedData } : article
        )
      );
      
      setTimeout(() => {
        resolve(true);
      }, 500);
    });
  };

  // 获取所有文章
  const getAllArticles = () => {
    return articles;
  };

  // 按年份分组
  const getArticlesByYear = () => {
    const grouped = {};
    articles.forEach(article => {
      const year = article.date.split('-')[0];
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(article);
    });
    return grouped;
  };

  // 获取文章详情
  const getArticleById = (id) => {
    return articles.find(article => article.id === parseInt(id));
  };

  return (
    <ArticleContext.Provider value={{
      articles,
      publishArticle,
      deleteArticle,
      updateArticle,
      getAllArticles,
      getArticlesByYear,
      getArticleById,
      totalArticles: articles.length
    }}>
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticles = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticles must be used within ArticleProvider');
  }
  return context;
};